/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
} from "@mui/material";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { fetchOverViewDetails } from "../../Services/redux/overView/OverViewDetailsActions";

const fieldLabelMap = {
  title: "App Name",
  categories: "Category",
  countries: "Countries",
  models: "Models",
  language: "Languages",
  ecuTypes: "ECU Types",
  contentVersion: "Latest Version",
  androidApiLevel: "API Level",
  shortText: "Short Description",
  longText: "Long Description",
  lastUpdateTime: "Last Modified Date",
  isPaymentEnabled: "Is Payment Enabled",
};

const DownloadReportPopup = ({ open, handleClose, currentFilters }) => {
  console.log("DownloadReportPopup props:", {
    open,
    handleClose,
    currentFilters,
  });
  const dispatch = useDispatch();

  const defaultSelectedFields = [
    "title",
    "categories",
    "androidApiLevel",
    "lastUpdateTime",
  ];

  const [selectedFields, setSelectedFields] = useState(defaultSelectedFields);
  const [downloadClicked, setDownloadClicked] = useState(false);
  const fullData = useSelector(
    (state) => state.overViewList.downloadOverViewData
  );
  const fullOverViewDetails = useSelector(
    (state) => state.overViewList.overviewDetails
  );
  const totalAppsCount =
    fullOverViewDetails?.catalogOverviewAppDTO?.totalAppsCount;

  const handleFieldChange = (event) => {
    setSelectedFields(event.target.value);
  };

  useEffect(() => {
    if (
      fullData?.catalogOverviewAppDTO &&
      fullData.catalogOverviewAppDTO.catalogOverviewAppBriefs.length > 0 &&
      downloadClicked
    ) {
      const updatedDataList =
        fullData?.catalogOverviewAppDTO?.catalogOverviewAppBriefs || [];
      console.log("Updated data list for download:", updatedDataList);
      
      const processedData = updatedDataList.map((fileObj) => {
        let ob = {};
        selectedFields.forEach((field) => {
          switch (field) {
            case "models":
            case "countries":
            case "language":
            case "ecuTypes":
            case "categories":
              ob[fieldLabelMap[field]] = fileObj[field]?.join(",") || "";
              break;
            case "title":
            case "contentVersion":
            case "androidApiLevel":
            case "shortText":
            case "longText":
              ob[fieldLabelMap[field]] = fileObj[field] || "";
              break;
            case "lastUpdateTime":
              ob["Last Modified Date"] = new Date(
                fileObj[field]
              ).toLocaleDateString();
              break;
            case "isPaymentEnabled":
              ob["Is Payment Enabled"] =
                fileObj.isPremiumApp && fileObj.isInAppPurchase;
              break;
            default:
              break;
          }
        });
        return ob;
      });

      const worksheet = XLSX.utils.json_to_sheet(processedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
      XLSX.writeFile(workbook, "report.xlsx");
      setDownloadClicked(false);
      handleClose();
    }
  }, [dispatch, fullData]);

  const handleDownload = () => {
    setDownloadClicked(true);
    try {
      // Use the filters from parent component
      const requestBody = {
        ...currentFilters,
      };
      console.log("Download request body:", requestBody);
      console.log("totalAppsCount", totalAppsCount);
      // Fetch latest data with all records before downloading
      dispatch(
        fetchOverViewDetails({
          isAppListNeeded: true,
          isCountNeeded: String(true).trim(),
          isAppCountNeeded: true,
          offset: 1,
          limit: totalAppsCount,
          searchText: "",
          requestBody,
          download: true,
        })
      );

      // Use the Redux state from top-level selector (fullData)
    } catch (error) {
      console.error("Error downloading report:", error);
    }
  };
  const handleCancel = () => {
    setSelectedFields(defaultSelectedFields);
    handleClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          padding: "20px",
          minWidth: "400px",
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{ fontSize: "1.2rem", fontWeight: "bold", padding: "10px 24px" }}
      >
        Download Report
      </DialogTitle>
      <DialogContent sx={{ paddingTop: "5px !important" }}>
        <FormControl fullWidth>
          <InputLabel
            id="select-field-label"
            sx={{
              fontSize: "0.9rem",
              fontWeight: "bold",
              color: "black",
              backgroundColor: "white",
              px: 0.5,
              transform: "translate(14px, -6px)",
              top: "-3px",
            }}
          >
            Select Field Attribute
          </InputLabel>
          <Select
            multiple
            value={selectedFields}
            onChange={handleFieldChange}
            displayEmpty
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                style: {
                  top: "400px !important",
                  maxHeight: 300,
                  overflow: "auto",
                },
              },
            }}
            renderValue={(selected) => {
              if (selected.length > 3) {
                return `${selected.slice(0, 3).join(", ")} ...`;
              }
              return selected.join(",");
            }}
            sx={{
              fontSize: "14px",
              borderRadius: "6px",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minHeight: "40px",
              },
            }}
          >
            {Object.entries(fieldLabelMap).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={selectedFields.includes(key)} />
                <ListItemText primary={value} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          sx={{
            textTransform: "none",
            background: "#1214151A",
            color: "#2A2C2D",
            padding: "8px 16px",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          sx={{
            textTransform: "none",
            fontSize: "0.9rem",
            padding: "8px 16px",
          }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DownloadReportPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  currentFilters: PropTypes.object.isRequired,
};

export default DownloadReportPopup;
